import { getHtmlMetaCharset } from '../html/charset';
import { detectBom } from '../str-buf/detectBom';
import { getHistoAndBasicAggs } from '../str-buf/getHistoAndBasicAggs';
import { getStrbufSummary } from '../str-buf/getStrbufSummary';
import { guessEncodingNoBom } from '../str-buf/guessEncodingNoBom';
import { StringDecoder } from '../types';

export function tryGetBodyStrEnc(bodyBuf: Buffer, charset: string | undefined): string | undefined {
	// Check for any BOM
	const bomEnc = detectBom(bodyBuf);
	if (bomEnc) {
		return bomEnc;
	}

	const aggs = getHistoAndBasicAggs(bodyBuf);
	const enc = guessEncodingNoBom(aggs, false);
	if (enc !== '8bit' && enc !== 'unknown') {
		return enc;
	}

	// Should already be trimmed and lowercased, but why not be sure
	const charset2 = (charset ?? '').replace(/[\s_-]+/g, '').toLowerCase();
	if (charset2 === 'unicode' || charset2 === 'unicodefeff') {
		// IE 11 allows saving with "unicode" charset... actually UTF-16LE
		// Other elements that have UTF-16BE encoding will be set to "unicodeFEFF" in mht output

		// Simple heuristic...
		if (charset2 === 'unicode' && aggs.numNulls > 0.4 * bodyBuf.length && bodyBuf[1] === 0) {
			return 'utf16le';
		} else if (
			charset2 === 'unicodefeff' &&
			aggs.numNulls > 0.4 * bodyBuf.length &&
			bodyBuf[0] === 0
		) {
			return 'utf16be';
		} else {
			throw new Error('Unable to verify "unicode" or "unicodeFEFF" charset');
		}
	}

	// Check for basic 7-bit ascii or utf-8
	{
		const summary = getStrbufSummary(bodyBuf);
		if (summary.allAscii) {
			return 'ascii';
		}
	}

	if (charset2 === 'iso88591') {
		// Same except for iso88591 controls
		return 'cp1252';
	} else if (charset) {
		return charset;
	}

	// Try to get from HTML
	const htmlStr = bodyBuf.toString('latin1');
	const charset3 = getHtmlMetaCharset(htmlStr);
	if (charset3) {
		const charset4 = charset3.replace(/[\s_-]+/g, '').toLowerCase();
		if (charset4 === 'iso88591') {
			// Same except for iso88591 controls
			return 'cp1252';
		} else {
			return charset3;
		}
	}
}

/*
function getBodyStrEnc(
    bodyBuf: Buffer,
    charset: string | undefined
): string {
    const enc = tryGetBodyStrEnc(bodyBuf, charset);
    if (!enc) {
        throw new Error('No charset for body');
    }
    return enc;
}

export function getBodyStr(
    bodyBuf: Buffer,
    contentTypeCharset: string | undefined,
    decode: StringDecoder
): string {
    const enc = getBodyStrEnc(bodyBuf, contentTypeCharset);

    const text = decode(bodyBuf, enc);
    return text;
}
*/

export interface BodyStrInfo {
	bodyEnc: string | undefined;
	bodyStr: string | undefined;
}

export function tryGetBodyStr(
	bodyBuf: Buffer,
	contentTypeCharset: string | undefined,
	decode: StringDecoder
): BodyStrInfo {
	const enc = tryGetBodyStrEnc(bodyBuf, contentTypeCharset);
	if (enc) {
		return {
			bodyEnc: enc,
			bodyStr: decode(bodyBuf, enc),
		};
	} else {
		return {
			bodyEnc: enc,
			bodyStr: undefined,
		};
	}
}
